import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  ChatCircleText, LinkSimple, ShieldCheck, Globe,
  Desktop, FileText, GearSix, Users,
  Lock, Buildings,
} from "@phosphor-icons/react";

const NAVY = "#0A0F2C";
const GOLD = "#C9A84C";

const FEATURES = [
  { Icon: ChatCircleText, title: "Ask in plain language",
    sub: "No SQL, no tickets, no training — just type your question and get answers instantly" },
  { Icon: LinkSimple, title: "Connects to every ERP",
    sub: "Oracle Fusion, SAP, Dynamics, NetSuite, Workday and more — one AI for all" },
  { Icon: ShieldCheck, title: "Enterprise-grade security",
    sub: "SOC 2 certified, UAE PDPL compliant — your data never leaves your instance" },
  { Icon: Globe, title: "Built for GCC & India",
    sub: "Arabic language support, AED/SAR/INR data natively — regional AI that understands your market" },
];

const STATS = [
  { num: "90%",  lbl: "Fewer IT tickets for finance reports" },
  { num: "4 hrs", lbl: "Saved per user per week on ERP tasks" },
  { num: "1 day", lbl: "To go live on your Oracle instance" },
];

const PERSONAS = [
  { Icon: Desktop, label: "For CFOs" },
  { Icon: FileText, label: "For CIOs" },
  { Icon: GearSix, label: "For CPOs" },
  { Icon: Users, label: "For teams" },
];

const SYSTEMS = [
  { name: "Oracle Fusion", hl: true },
  { name: "SAP S/4HANA" }, { name: "MS Dynamics" }, { name: "NetSuite" },
  { name: "Workday" }, { name: "+ more" },
];

export default function ChangePasswordPage() {
  const { changePassword, logout, user } = useAuth();
  const nav = useNavigate();

  const [current, setCurrent]   = useState("");
  const [next, setNext]         = useState("");
  const [confirm, setConfirm]   = useState("");
  const [showCur, setShowCur]   = useState(false);
  const [showNew, setShowNew]   = useState(false);
  const [err, setErr]           = useState("");
  const [loading, setLoading]   = useState(false);

  const validate = () => {
    if (!current)           return "Enter your temporary password.";
    if (next.length < 8)    return "New password must be at least 8 characters.";
    if (next !== confirm)   return "Passwords do not match.";
    if (next === current)   return "New password must be different from the temporary one.";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const vErr = validate();
    if (vErr) { setErr(vErr); return; }
    setErr(""); setLoading(true);
    try {
      await changePassword(current, next);
      nav("/dashboard", { replace: true });
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Password change failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fieldStyle = {
    border: "1px solid #DDE1E9", backgroundColor: "#FAFAFA", color: "#0D1117",
  };
  const onFocus = (e) => { e.currentTarget.style.borderColor = NAVY; e.currentTarget.style.backgroundColor = "#fff"; };
  const onBlur  = (e) => { e.currentTarget.style.borderColor = "#DDE1E9"; e.currentTarget.style.backgroundColor = "#FAFAFA"; };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-[#F0F2F5] py-10 px-4 font-sans">
      <div className="w-full max-w-[1080px] grid grid-cols-1 lg:grid-cols-[56%_44%] rounded-2xl overflow-hidden shadow-[0_24px_64px_rgba(0,0,0,0.18)] bg-white"
        data-testid="change-password-shell">

        {/* ─────── LEFT (navy) — identical to LoginPage ─────── */}
        <aside className="hidden lg:flex flex-col gap-6 p-11" style={{ backgroundColor: NAVY }} data-testid="login-left">
          <div className="flex items-center gap-3">
            <div className="w-[56px] h-[56px] rounded-xl flex items-center justify-center text-[28px] font-semibold"
              style={{ backgroundColor: GOLD, color: NAVY }}>V</div>
            <div className="text-[26px] font-semibold text-white tracking-[1.5px]">
              VĀ<span style={{ color: GOLD }}>KU</span>
            </div>
          </div>

          <h1 className="text-[26px] font-semibold text-white leading-[1.35]" data-testid="login-headline">
            Your enterprise systems,<br />
            finally speaking <span style={{ color: GOLD }}>plain English.</span>
          </h1>

          <p className="text-[13.5px] leading-[1.75] max-w-[360px]" style={{ color: "#8B93B8" }}>
            VĀKU replaces complex ERP interfaces with a single AI layer — so your CFO, CIO, and every team member gets instant answers from Oracle, SAP, and every system they run.
          </p>

          <div className="flex flex-col gap-[13px]">
            {FEATURES.map(({ Icon, title, sub }) => (
              <div key={title} className="flex items-start gap-2.5">
                <div className="w-[30px] h-[30px] rounded-[7px] flex items-center justify-center shrink-0"
                  style={{ backgroundColor: "rgba(201,168,76,0.13)" }}>
                  <Icon size={16} weight="regular" style={{ color: GOLD }} />
                </div>
                <div>
                  <div className="text-[13px] font-semibold mb-0.5" style={{ color: "#E0E6FF" }}>{title}</div>
                  <div className="text-[11.5px] leading-[1.5]" style={{ color: "#8B93B8" }}>{sub}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-2.5">
            {STATS.map((s) => (
              <div key={s.num} className="flex-1 rounded-[10px] px-3.5 py-3.5"
                style={{ backgroundColor: "rgba(255,255,255,0.055)", borderWidth: "0.5px",
                          borderStyle: "solid", borderColor: "rgba(255,255,255,0.1)" }}>
                <div className="text-[22px] font-semibold leading-none mb-1.5" style={{ color: GOLD }}>{s.num}</div>
                <div className="text-[10.5px] leading-[1.4]" style={{ color: "#8B93B8" }}>{s.lbl}</div>
              </div>
            ))}
          </div>

          <blockquote className="rounded-r-lg px-4 py-3.5" style={{ backgroundColor: "rgba(255,255,255,0.04)", borderLeft: `2.5px solid ${GOLD}` }}>
            <p className="text-[12.5px] italic leading-[1.65] mb-2" style={{ color: "#C8D0E8" }}>
              &ldquo;We used to raise 3 tickets a day just to get basic headcount reports. With VĀKU, our HR director gets answers in seconds — no IT involvement.&rdquo;
            </p>
            <div className="text-[11px]" style={{ color: "#8B93B8" }}>
              <strong style={{ color: GOLD, fontStyle: "normal" }}>CFO, UAE-based manufacturing group</strong> — Oracle Fusion customer
            </div>
          </blockquote>

          <div>
            <div className="text-[10px] uppercase tracking-[0.9px] mb-2" style={{ color: "#5A6180" }}>Connects to</div>
            <div className="flex flex-wrap gap-[7px]">
              {SYSTEMS.map((s) => (
                <span key={s.name} className="rounded-md px-2.5 py-1 text-[10.5px] font-medium"
                  style={{
                    backgroundColor: "rgba(255,255,255,0.055)",
                    borderWidth: "0.5px", borderStyle: "solid",
                    borderColor: s.hl ? "rgba(201,168,76,0.35)" : "rgba(255,255,255,0.1)",
                    color: s.hl ? GOLD : "#8B93B8",
                  }}>
                  {s.name}
                </span>
              ))}
            </div>
          </div>
        </aside>

        {/* ─────── RIGHT (white form) — change-password content ─────── */}
        <section className="flex flex-col justify-center px-9 py-11 bg-white" data-testid="change-password-right">
          {/* Mobile-only logo */}
          <div className="lg:hidden flex items-center gap-3 mb-6">
            <div className="w-[56px] h-[56px] rounded-xl flex items-center justify-center text-[28px] font-semibold"
              style={{ backgroundColor: GOLD, color: NAVY }}>V</div>
            <div className="text-[26px] font-semibold tracking-[1.5px]" style={{ color: NAVY }}>
              VĀ<span style={{ color: GOLD }}>KU</span>
            </div>
          </div>

          <div className="flex flex-wrap gap-[7px] mb-6">
            <span className="rounded-[20px] px-2.5 py-1 text-[11px] flex items-center gap-1"
              style={{
                borderWidth: "0.5px", borderStyle: "solid",
                borderColor: "rgba(201,168,76,0.5)",
                backgroundColor: "rgba(201,168,76,0.08)",
                color: "#8A6B1A",
              }}>
              <Lock size={12} weight="regular" style={{ color: "#8A6B1A" }} />
              First-time setup
            </span>
          </div>

          <h2 className="text-[22px] font-semibold mb-1" style={{ color: "#0D1117" }} data-testid="change-password-title">
            Set your password
          </h2>
          <p className="text-[13.5px] mb-3" style={{ color: "#666" }}>
            You&apos;re signed in with a temporary password. Choose a permanent one to continue.
          </p>

          {user?.name && (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md mb-6 w-fit"
              style={{ backgroundColor: "#FAFAFA", border: "1px solid #DDE1E9" }}>
              <span className="text-[11px] uppercase tracking-[0.12em]" style={{ color: "#999" }}>
                Signed in as
              </span>
              <span className="text-[13px] font-semibold" style={{ color: "#0D1117" }}>{user.name}</span>
            </div>
          )}

          <form onSubmit={submit} className="space-y-[15px]" data-testid="change-password-form">
            {/* Current (temporary) password */}
            <div>
              <label className="block text-[12.5px] font-medium mb-1.5" style={{ color: "#444" }}>
                Temporary password
              </label>
              <div className="relative">
                <input
                  data-testid="current-password"
                  type={showCur ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  className="w-full h-10 rounded-lg px-3.5 text-[13.5px] outline-none transition-colors"
                  style={{ ...fieldStyle, paddingRight: "48px" }}
                  onFocus={onFocus} onBlur={onBlur}
                />
                <button type="button"
                  onClick={() => setShowCur((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[12px]"
                  style={{ color: "#999", background: "none", border: "none", cursor: "pointer" }}>
                  {showCur ? "Hide" : "Show"}
                </button>
              </div>
            </div>

            {/* New password */}
            <div>
              <label className="block text-[12.5px] font-medium mb-1.5" style={{ color: "#444" }}>
                New password
              </label>
              <div className="relative">
                <input
                  data-testid="new-password"
                  type={showNew ? "text" : "password"}
                  required
                  autoComplete="new-password"
                  value={next}
                  onChange={(e) => setNext(e.target.value)}
                  className="w-full h-10 rounded-lg px-3.5 text-[13.5px] outline-none transition-colors"
                  style={{ ...fieldStyle, paddingRight: "48px" }}
                  onFocus={onFocus} onBlur={onBlur}
                />
                <button type="button"
                  onClick={() => setShowNew((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[12px]"
                  style={{ color: "#999", background: "none", border: "none", cursor: "pointer" }}>
                  {showNew ? "Hide" : "Show"}
                </button>
              </div>
              <p className="text-[11.5px] mt-1" style={{ color: "#999" }}>Minimum 8 characters</p>
            </div>

            {/* Confirm */}
            <div>
              <label className="block text-[12.5px] font-medium mb-1.5" style={{ color: "#444" }}>
                Confirm new password
              </label>
              <input
                data-testid="confirm-password"
                type="password"
                required
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full h-10 rounded-lg px-3.5 text-[13.5px] outline-none transition-colors"
                style={fieldStyle}
                onFocus={onFocus} onBlur={onBlur}
              />
            </div>

            {err && (
              <div className="text-[12px] font-semibold text-[#DC2626]" data-testid="change-password-error">
                {err}
              </div>
            )}

            <button
              data-testid="change-password-submit"
              type="submit" disabled={loading}
              className="w-full h-[42px] rounded-lg text-[14px] font-semibold tracking-[0.3px] text-white transition-colors disabled:opacity-60"
              style={{ backgroundColor: NAVY }}
              onMouseOver={(e) => !loading && (e.currentTarget.style.backgroundColor = "#141B40")}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = NAVY)}
            >
              {loading ? "Saving…" : "Set password & continue"}
            </button>
          </form>

          <div className="flex justify-center flex-wrap gap-4 mt-6">
            <span className="flex items-center gap-1.5 text-[11px]" style={{ color: "#999" }}>
              <Lock size={13} weight="regular" style={{ color: "#999" }} /> SSL secured
            </span>
            <span className="flex items-center gap-1.5 text-[11px]" style={{ color: "#999" }}>
              <ShieldCheck size={13} weight="regular" style={{ color: "#999" }} /> SOC 2 certified
            </span>
            <span className="flex items-center gap-1.5 text-[11px]" style={{ color: "#999" }}>
              <Globe size={13} weight="regular" style={{ color: "#999" }} /> GDPR & UAE PDPL
            </span>
          </div>

          <div className="text-center mt-5">
            <button
              onClick={async () => { await logout(); nav("/login"); }}
              data-testid="change-password-logout"
              className="text-[12px] hover:underline"
              style={{ color: "#999", background: "none", border: "none", cursor: "pointer" }}
            >
              Sign out
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}